import ResponsiveNav from "@rsuite/responsive-nav";
import classNames from "classnames";
import { useState } from "react";
import CDNLink from "../../../../../components/CDNLink/CDNLink";
import PDFViewer from "../../../../../components/PDFViewer/PDFViewer";
import i18n from "../../../../../i18n";
import { useViewportSelector } from "../../../../../redux/hooks";
import InvoiceService from "../../InvoiceService";
import { Invoice } from "../../RAInterfaces";
import "./RADuplicateCheck.scss";

interface RADuplicateCheckProps {
  original: Invoice;
  duplicates: Invoice[];
}
const RADuplicateCheck = (props: RADuplicateCheckProps) => {
  const vpSelector = useViewportSelector();
  const [activeKey, setActiveKey] = useState(props.duplicates[0]._id);

  const sideBySide = !["xs", "sm", "md"].includes(vpSelector);

  const cdnOriginal = props.original.data.invoice.file;
  const selectedCompareItem = [
    ...(sideBySide ? [] : [props.original]),
    ...props.duplicates,
  ].find((e) => e._id === activeKey);
  const cdnCompareItem = selectedCompareItem
    ? selectedCompareItem?.data.invoice.file
    : undefined;

  const cdnDataOriginal = {
    filename: InvoiceService.generateFilenameForInvoice(props.original),
    assetField: "data.invoice.file",
    assetId: props.original._id,
    assetType: "invoice",
    cdnData: cdnOriginal,
    cdnId: cdnOriginal.id,
  };
  const cdnDataDuplicate = selectedCompareItem
    ? {
        filename:
          InvoiceService.generateFilenameForInvoice(selectedCompareItem),
        assetField: "data.invoice.file",
        assetId: selectedCompareItem._id,
        assetType: "invoice",
        cdnData: cdnCompareItem,
        cdnId: cdnCompareItem.id,
      }
    : undefined;

  return (
    <div className={classNames(`ra-duplicate-check`)}>
      <div className={`description`}>
        {i18n.t("ra:DuplicateCheck.Description")}
      </div>
      <div className={`compare`}>
        {sideBySide && (
          <div className={`original compare-item`}>
            <div className={`title`}>
              {i18n.t("ra:DuplicateCheck.Original")}
            </div>
            <div className={`content`}>
              {cdnDataOriginal && (
                <CDNLink
                  assetType={cdnDataOriginal.assetType}
                  assetId={cdnDataOriginal.assetId}
                  assetField={cdnDataOriginal.assetField}
                  cdnId={cdnDataOriginal.cdnId}
                  filename={cdnDataOriginal.cdnData.filename}
                  fileKey={cdnDataOriginal.cdnData.key}
                  hasFolderReadPermissions={true}
                  render={(url) => (
                    <PDFViewer
                      url={url}
                      zoomFactorStorageKey={
                        "pdf_zoom_factor_duplicate_original"
                      }
                      filename={cdnDataOriginal.filename}
                      scrollGroup="ra-duplicate-pdf-view"
                      scrollIdentifier="original"
                    />
                  )}
                />
              )}
            </div>
          </div>
        )}
        <div
          className={`duplicate compare-item ${
            !sideBySide ? "no-padding" : ""
          }`}
        >
          <ResponsiveNav
            appearance="subtle"
            activeKey={activeKey}
            onSelect={(key: string) => (key ? setActiveKey(key) : undefined)}
          >
            {!sideBySide && (
              <ResponsiveNav.Item eventKey={props.original._id}>
                {i18n.t("ra:DuplicateCheck.Original")}
              </ResponsiveNav.Item>
            )}
            {props.duplicates.map((duplicate, index) => {
              return (
                <ResponsiveNav.Item
                  eventKey={duplicate._id}
                  key={duplicate._id}
                >
                  {i18n.t("ra:DuplicateCheck.Duplicate")} {index + 1}
                </ResponsiveNav.Item>
              );
            })}
          </ResponsiveNav>
          <div className={`content`}>
            {cdnDataDuplicate && (
              <CDNLink
                assetType={cdnDataDuplicate.assetType}
                assetId={cdnDataDuplicate.assetId}
                assetField={cdnDataDuplicate.assetField}
                cdnId={cdnDataDuplicate.cdnId}
                filename={cdnDataDuplicate.cdnData.filename}
                fileKey={cdnDataDuplicate.cdnData.key}
                hasFolderReadPermissions={true}
                render={(url) => (
                  <PDFViewer
                    url={url}
                    zoomFactorStorageKey={"pdf_zoom_factor_duplicate_compare"}
                    filename={cdnDataDuplicate.filename}
                    scrollGroup="ra-duplicate-pdf-view"
                    scrollIdentifier="compare"
                  />
                )}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RADuplicateCheck;
